// journal/yourTexts — the journal-wide settings for the author's own texts:
// may the assistant read them, and remove them all. Shown ONCE, in Settings →
// Assistant, next to the summary consent: both answer "what may the assistant
// do with what I write", so every AI consent sits in one place (the Journal
// keeps only a quiet link here). Pure: renders `projectYourTexts` and reports
// the journal module's own msgs, so the module's update stays the one
// implementation.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../../components/Button/Button.js";
import { SettingsRow } from "../../components/Settings/SettingsRow/SettingsRow.js";
import { Switch } from "../../components/Switch/Switch.js";
import { Text } from "../../components/Text/Text.js";
import type { JournalMsg } from "./msg.js";
import type { YourTextsView } from "./view-model.js";

export const YourTexts = ({
    view,
    send,
}: {
    readonly view: YourTextsView;
    readonly send: (msg: JournalMsg) => void;
}): ReactNode => {
    const { t } = useLingui();
    const { assistantReadsOwn, eraseAll, problem } = view;
    return (
        <>
            <SettingsRow
                label={t`Read my journal texts`}
                description={t`The assistant reads what you write in the journal when it summarizes your days.`}
                control={
                    <Switch
                        checked={assistantReadsOwn}
                        onCheckedChange={(allow) =>
                            send({
                                type: allow
                                    ? "reading_allowed"
                                    : "reading_disallowed",
                            })
                        }
                        label={t`Read my journal texts`}
                    />
                }
            />
            <SettingsRow
                label={t`Remove all your texts from the journal`}
                // Honest (D21): the stored history keeps the texts.
                description={t`They remain in the workspace's stored history until you delete your account.`}
                control={
                    eraseAll === "confirming" ? (
                        <span>
                            <Button
                                variant="danger"
                                onClick={() =>
                                    send({ type: "erase_all_confirmed" })
                                }
                            >
                                {t`Remove all`}
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={() =>
                                    send({ type: "erase_all_cancelled" })
                                }
                            >
                                {t`Cancel`}
                            </Button>
                        </span>
                    ) : (
                        <Button
                            disabled={eraseAll === "erasing"}
                            onClick={() => send({ type: "erase_all_asked" })}
                        >
                            {eraseAll === "erasing" ? t`Removing…` : t`Remove…`}
                        </Button>
                    )
                }
            />
            {problem !== null ? (
                <Text as="p" size="xs" tone="danger">
                    {problem === "consent"
                        ? t`The setting could not be saved.`
                        : t`Your texts could not be removed.`}
                </Text>
            ) : null}
        </>
    );
};
